import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { NavDropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import sanityClient from "../../sanityClient";

// Lazy load MobileMenu component
const MobileMenu = React.lazy(() => import("./MobileMenu"));

// Constants
const SERVICES_QUERY = `*[_type == "service" && showOnMenuBar == true]{
  title,
  slug
}`;

const PHONE_NUMBER = "+917042112226";

const Header = () => {
  // Refs
  const controllerRef = useRef(null);
  
  // State management
  const [headerState, setHeaderState] = useState({
    isSticky: false,
    isOpen: false,
    isHovered: false,
    services: [],
    loading: true,
    error: null
  });

  // Redux selectors
  const { isServiceDetailPage, isServiceMobile, nameStickyService } = useSelector(
    state => state.service
  );
  const { isCareerDetailPage } = useSelector(state => state.careerDetail);
  const { isThankYouPage } = useSelector(state => state.isThank);
  const { isPrivacyPolicyPage } = useSelector(state => state.privacyPolicy);
  const { isTermPage } = useSelector(state => state.terms);

  // Memoized callbacks
  const handleMouseEnter = useCallback(() => {
    setHeaderState(prev => ({ ...prev, isHovered: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHeaderState(prev => ({ ...prev, isHovered: false }));
  }, []);

  const handleToggleMenu = useCallback(() => {
    setHeaderState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => {
      setHeaderState(prev => ({
        ...prev,
        isSticky: window.scrollY > 0
      }));
    });
  }, []);

  // Fetch services data
  useEffect(() => {
    const fetchServices = async () => {
      try {
        if (controllerRef.current) {
          controllerRef.current.abort();
        }
        controllerRef.current = new AbortController();

        const data = await sanityClient.fetch(SERVICES_QUERY, {}, {
          signal: controllerRef.current.signal,
          cache: 'force-cache'
        });

        setHeaderState(prev => ({
          ...prev,
          services: data,
          loading: false,
          error: null
        }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        
        setHeaderState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load services'
        }));
        console.error("Error fetching services:", error);
      }
    };

    fetchServices();

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // Scroll event listener
  useEffect(() => {
    const throttledScroll = () => {
      let ticking = false;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });
    return () => window.removeEventListener("scroll", throttledScroll);
  }, [handleScroll]);

  // Memoized values
  const headerClasses = useMemo(() => {
    const classes = ['header-area'];
    if (headerState.isSticky) classes.push('stick');
    if (isThankYouPage || isCareerDetailPage || isTermPage || isPrivacyPolicyPage) {
      classes.push('white-header');
    }
    if (isServiceDetailPage && isServiceMobile && nameStickyService) {
      classes.push('service-detail-class');
    }
    return classes.join(' ');
  }, [
    headerState.isSticky,
    isThankYouPage,
    isCareerDetailPage,
    isTermPage,
    isPrivacyPolicyPage,
    isServiceDetailPage,
    isServiceMobile,
    nameStickyService
  ]);

  const serviceLinks = useMemo(() => 
    headerState.services.map(service => (
      <Link
        key={service.slug.current}
        to={`/${service.slug.current}`}
        onClick={handleMouseLeave}
      >
        {service.title}
      </Link>
    )),
    [headerState.services, handleMouseLeave]
  );

  return (
    <>
      <header className={headerClasses}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 col-md-4 hide-mobile">
              <nav className="header-menulink">
                <ul>
                  <li><Link to="/">home</Link></li>
                  <li><Link to="/about-us">about</Link></li>
                  <li 
                    onMouseEnter={handleMouseEnter} 
                    onMouseLeave={handleMouseLeave}
                  >
                    <NavDropdown
                      id="services-dropdown"
                      title="Services"
                      menuVariant="light"
                      show={headerState.isHovered}
                    >
                      <div className="drop-menulinks">
                        {serviceLinks}
                      </div>
                    </NavDropdown>
                  </li>
                </ul>
              </nav>
            </div>

            <div className="col-12 col-lg-2 col-md-4">
              <div className="logo">
                <Link to="/">
                  <img 
                    src="/assets/home/logo-black.svg" 
                    alt="Logo"
                    width="100%"
                    height="100%"
                    loading="eager"
                  />
                </Link>
              </div>
            </div>

            <div className="col-lg-5 col-md-4 rightside-menu">
              <nav className="header-menulink">
                <ul>
                  <li><Link to="/project">projects</Link></li>
                  <li><Link to="/media">media</Link></li>
                  <li><Link to="/contact-us">contact us</Link></li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </header>

      <nav className="mobile-menu">
        <Link to="/">
          <img src="/assets/home/home.svg" alt="home" loading="lazy" />
        </Link>
        <Link to="/service-listing">
          <img src="/assets/home/services.svg" alt="services" loading="lazy" />
        </Link>
        <a href={`tel:${PHONE_NUMBER}`}>
          <img src="/assets/home/call.svg" alt="call" loading="lazy" />
        </a>
        <Link to="/project">
          <img src="/assets/home/project.svg" alt="project" loading="lazy" />
        </Link>
        <button onClick={handleToggleMenu} aria-label="Toggle menu">
          <img src="/assets/home/menu.svg" alt="" loading="lazy" />
        </button>
      </nav>

      <React.Suspense fallback={<div className="loading"></div>}>
        <MobileMenu 
          isOpen={headerState.isOpen} 
          setIsOpen={(value) => setHeaderState(prev => ({ ...prev, isOpen: value }))} 
          services={headerState.services} 
        />
      </React.Suspense>
    </>
  );
};

Header.displayName = 'Header';

export default React.memo(Header);